import React, { useState } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@hipotecar/ui/lib/utils";

import avatar1 from "@/assets/illustrations/avatars/avatar-1.svg";
import avatar2 from "@/assets/illustrations/avatars/avatar-2.svg";
import avatar3 from "@/assets/illustrations/avatars/avatar-3.svg";
import avatar4 from "@/assets/illustrations/avatars/avatar-4.svg";
import avatar5 from "@/assets/illustrations/avatars/avatar-5.svg";

const FALLBACK_AVATARS = [avatar1, avatar2, avatar3, avatar4, avatar5];

const avatarVariants = cva(
	"relative inline-flex items-center justify-center overflow-hidden rounded-full border bg-gray-100 dark:bg-gray-800",
	{
		variants: {
			size: {
				xs: "h-6 w-6 text-xs",
				sm: "h-8 w-8 text-sm",
				md: "h-10 w-10 text-base",
				lg: "h-12 w-12 text-lg",
				xl: "h-16 w-16 text-xl",
				"2xl": "h-20 w-20 text-2xl",
			},
			variant: {
				default: "border-gray-200 dark:border-gray-800",
				primary: "border-primary-200 dark:border-primary-800",
			},
		},
		defaultVariants: {
			size: "md",
			variant: "default",
		},
	},
);

export interface AvatarProps
	extends
		React.HTMLAttributes<HTMLDivElement>,
		VariantProps<typeof avatarVariants> {
	/** Image URL for the avatar */
	src?: string | null;
	/** Alt text for the image */
	alt?: string;
	/** Name to generate initials from (fallback when image fails) */
	name?: string | null;
	/** Custom fallback content */
	fallback?: React.ReactNode;
}

/**
 * Avatar component with automatic fallback to initials or icon
 *
 * @example
 * ```tsx
 * <Avatar src={user.avatar_url} name={user.full_name} size="lg" />
 * ```
 */
export default function Avatar({
	src,
	alt,
	name,
	fallback,
	size,
	variant,
	className,
	...props
}: AvatarProps) {
	const [imageError, setImageError] = useState(false);

	const getFallbackAvatar = (name: string): string => {
		let hash = 0;
		for (let i = 0; i < name.length; i++) {
			hash = name.charCodeAt(i) + ((hash << 5) - hash);
		}
		const index = Math.abs(hash) % FALLBACK_AVATARS.length;
		return FALLBACK_AVATARS[index];
	};

	const showImage = src && !imageError;
	// const initials = name ? getInitials(name) : "";

	return (
		<div
			className={cn(avatarVariants({ size, variant }), className)}
			{...props}
		>
			{showImage ? (
				<img
					src={src}
					alt={alt || name || "Avatar"}
					className="size-full object-cover"
					onError={() => setImageError(true)}
				/>
			) : fallback ? (
				fallback
			) : (
				<img
					src={getFallbackAvatar(name || "User")}
					alt={alt || name || "Avatar"}
					className="size-full object-cover"
				/>
			)}
		</div>
	);
}
